import { clearEmptyProps } from '@kutil';
import merge from 'lodash/merge';
import { UserType } from '../enums';
import { SOCIAL_SSO, SSO_PROVIDERS } from './auth-provider-state.model';
import { Deserializable } from './deserializable.model';
import { Group } from './og.model';
import { Settings } from './settings.model';
import { SubscriptionPlan } from './subscription-plan.model';
import { UserFeature } from './user-feature.model';
import { UserRole } from './user-role.model';

export class User implements Deserializable<any, User> {
  type: UserType;

  constructor(
    public id?: string,
    public email?: string,
    public firstName?: string,
    public lastName?: string,
    public nickname?: string,
    public features?: UserFeature[],
    public isSsoUser?: boolean,
    public provider?: string,
    private _type?: string,
    public error?: any,
    public group?: Group,
    public groupId?: string,
    public settings?: Settings,
    public subscriptionPlan?: SubscriptionPlan,
    public subscriptionPlanEmail?: string,
    public proposedEmail?: string,
    public roles?: UserRole[],
    public unconfirmedEmail?: string,
    public dateDeviceCreated?: Date
  ) {
    this.type = UserType[_type as keyof typeof UserType];
  }

  deserialize(input: any): User {
    if (input == null) {
      return null;
    }

    this.id = input['id'];
    this.email = input['email'];
    this.firstName = input['firstName'];
    this.lastName = input['lastName'];
    this.nickname = input['nickname'];
    this.features = input['features']?.map?.((feature) => new UserFeature().deserialize(feature));
    if (input['provider']) {
      this.isSsoUser = determineIfSsoUser(input['provider']);
      this.provider = input['provider'];
    }
    this.type = input['type'];
    this.error = input['error'];
    this.group = new Group().deserialize(input['group']);
    this.groupId = input['groupId'];
    this.settings = new Settings().deserialize(input['settings']);
    this.subscriptionPlan = new SubscriptionPlan().deserialize(input['subscriptionPlan']);
    this.subscriptionPlanEmail = input['subscriptionPlanEmail'];
    this.proposedEmail = input['proposedEmail'];
    this.roles = input['roles']?.map?.((role) => new UserRole().deserialize(role));
    this.unconfirmedEmail = input['unconfirmedEmail'];
    this.dateDeviceCreated = input['dateDeviceCreated'] ? new Date(input['dateDeviceCreated']) : new Date();

    return this;
  }

  combine(updatedUser: User): User {
    if (!updatedUser) {
      return this;
    }
    const cleanedUser: Partial<User> = clearEmptyProps(this);
    const cleanedUpdatedUser: Partial<User> = clearEmptyProps(updatedUser);
    const allFeatures = [...(cleanedUser.features || []), ...(cleanedUpdatedUser.features || [])];
    const combinedUniqueFeatures = [
      ...new Map(allFeatures.map((feature) => [feature['key'], feature])).values(),
    ].filter((feature) => Object.values(feature).some((featureValues) => !!featureValues));
    const features = combinedUniqueFeatures.length > 0 ? combinedUniqueFeatures : undefined;
    let group = cleanedUser.group;
    if (group && cleanedUpdatedUser.group) {
      const liveSupport = merge(
        clearEmptyProps(group?.liveSupport),
        clearEmptyProps(cleanedUpdatedUser.group?.liveSupport)
      );
      group = new Group().deserialize({ ...group, ...cleanedUpdatedUser.group, liveSupport });
    }
    return new User().deserialize({ ...cleanedUser, ...cleanedUpdatedUser, ...{ features, group } });
  }

  get providerLabel(): string {
    return this.provider === SOCIAL_SSO.SignInWithApple ? 'Apple' : this.provider;
  }
}

function determineIfSsoUser(provider) {
  return SSO_PROVIDERS.includes(provider);
}
